import React from 'react';
import { FormGroup, Input, InputGroup, InputGroupText } from 'reactstrap';
import ValidatedSelect, {
    ReactSelectStyles,
} from '../common/forms/ValidatedSelect';
import { FormLabel } from '../common/forms/FormElements';
import { isAlwaysAvailable } from '../contracts/ContractPhase';
import _ from 'lodash';
import cls from 'classnames';
import { v4 as uuid } from 'uuid';
import ContractPhaseOverageWarning from './ContractPhaseOverageWarning';

export default class EntityContractPhaseManager extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            isValid: true,
            managedEntityContractPhases: props.entityContractPhases || [],
            guid: uuid(),
            entityId: props.entityId || null,
        };
    }

    onInternalContractPhaseChange = (selectedContractPhases) => {
        let { managedEntityContractPhases } = this.state;

        managedEntityContractPhases = managedEntityContractPhases.filter(
            (ecp) =>
                selectedContractPhases.some(
                    (scp) => scp.id == ecp.contractPhaseId
                )
        );

        const newManagedContractPhases = selectedContractPhases.filter(
            (scp) =>
                !managedEntityContractPhases.some(
                    (ecp) => ecp.contractPhaseId == scp.id
                )
        );

        if (newManagedContractPhases && newManagedContractPhases.length) {
            managedEntityContractPhases = managedEntityContractPhases.concat(
                newManagedContractPhases.map((ecp) => {
                    return { contractPhaseId: ecp.id, targetQuantity: '' };
                })
            );
        }

        const isValid = this.areEntriesValid(managedEntityContractPhases);

        this.setState({ isValid, managedEntityContractPhases }, () => {
            this.signalUpdate();
        });
    };

    isEntityPhaseVistaManagementPhase = (managedEntityContractPhase) => {
        const { contractPhases } = this.props;

        const contractPhase = contractPhases.find(
            (cp) => cp.id == managedEntityContractPhase.contractPhaseId
        );

        return contractPhase && isAlwaysAvailable(contractPhase);
    };

    areEntriesValid = (managedEntityContractPhases) =>
        managedEntityContractPhases.every(
            (ecp) =>
                (ecp.targetQuantity !== '' && ecp.targetQuantity >= 0) ||
                this.isEntityPhaseVistaManagementPhase(ecp)
        );

    signalUpdate = () => {
        const { managedEntityContractPhases, isValid } = this.state;
        const { onEntityContractPhaseChange } = this.props;

        onEntityContractPhaseChange({
            entityContractPhases: managedEntityContractPhases,
            isValid,
        });
    };

    onInternalTargetQuantityChange = (dataContainer, e) => {
        const { managedEntityContractPhases } = this.state;

        const { contractPhases } = this.props;

        const originatingEntityContractPhase =
            dataContainer.entityContractPhase;

        const index = _.findIndex(
            managedEntityContractPhases,
            (ecp) =>
                ecp.contractPhaseId ==
                originatingEntityContractPhase.contractPhaseId
        );

        const contractPhase = contractPhases.find(
            (cp) => cp.id == originatingEntityContractPhase.contractPhaseId
        );

        const isLumpSum = contractPhase && contractPhase.unitOfMeasure === 'LS';

        const newQuantity =
            isLumpSum && !isNaN(e.target.value)
                ? Number(e.target.value) / 100
                : e.target.value;

        managedEntityContractPhases[index].targetQuantity = newQuantity;

        const isValid = this.areEntriesValid(managedEntityContractPhases);

        this.setState({ managedEntityContractPhases, isValid }, () => {
            this.signalUpdate();
        });
    };

    componentDidUpdate(prevProps) {
        const { entityId } = this.state;

        if (
            !this.props.entityContractPhases ||
            (prevProps.entityContractPhases.length ==
                this.props.entityContractPhases.length &&
                this.props.entityId == entityId)
        ) {
            return;
        }

        this.setState({
            managedEntityContractPhases: this.props.entityContractPhases,
            entityId: this.props.entityId,
            contractPhases: this.props.contractPhases,
        });
    }

    getOrderedEntityContractPhases = () => {
        const { managedEntityContractPhases } = this.state;

        if (!managedEntityContractPhases) {
            return managedEntityContractPhases;
        }

        const { contractPhases } = this.props;

        managedEntityContractPhases.sort((a, b) => {
            const contractPhaseA = contractPhases.find(
                (cp) => a.contractPhaseId == cp.id
            );
            const contractPhaseB = contractPhases.find(
                (cp) => b.contractPhaseId == cp.id
            );

            if (contractPhaseA && !contractPhaseB) {
                return -1;
            }

            if (
                (!contractPhaseA && !contractPhaseB) ||
                contractPhaseA.displayPhase == contractPhaseB.displayPhase
            ) {
                return 0;
            }

            return contractPhaseA.displayPhase < contractPhaseB.displayPhase
                ? -1
                : 1;
        });

        return managedEntityContractPhases;
    };

    getQuantityQualifier = (contractPhase) => {
        const { showBidTotal, existingEntityContractPhases } = this.props;

        if (!showBidTotal) {
            if (
                !existingEntityContractPhases ||
                existingEntityContractPhases.some(
                    (ecp) => ecp.contractPhaseId == contractPhase.id
                )
            ) {
                return null;
            }
        }

        const isLumpSum = contractPhase.unitOfMeasure === 'LS';

        const bidQuantity = isLumpSum
            ? `${contractPhase.quantity * 100}%`
            : contractPhase.quantity;

        const scheduledQuantity = isLumpSum
            ? `${contractPhase.scheduledQuantity * 100}%`
            : contractPhase.scheduledQuantity;

        const scheduledBreakdown = contractPhase.scheduledQuantity
            ? ` ${scheduledQuantity} ${contractPhase.unitOfMeasure} has been scheduled elsewhere.`
            : null;

        return (
            <>
                . The bid estimated {bidQuantity} {contractPhase.unitOfMeasure}.
                {scheduledBreakdown}
            </>
        );
    };

    render() {
        const { contractPhases, labelText, disabled } = this.props;
        const { uuid } = this.state;

        const managedEntityContractPhases =
            this.getOrderedEntityContractPhases();

        return (
            <>
                <FormGroup className="entity-contract-phase-manager">
                    {labelText && (
                        <FormLabel
                            htmlFor="entityContractPhases"
                            text={labelText}
                        />
                    )}
                    <ValidatedSelect
                        name="entityContractPhases"
                        options={contractPhases}
                        getOptionValue={(co) => co.id}
                        getOptionLabel={(co) =>
                            `${co.displayPhase} - ${co.description}`
                        }
                        isMulti
                        value={
                            contractPhases.filter((co) =>
                                managedEntityContractPhases.some(
                                    (ecp) => ecp.contractPhaseId == co.id
                                )
                            ) ?? ''
                        }
                        onChange={this.onInternalContractPhaseChange}
                        hideClear
                        menuPortalTarget={
                            typeof document !== 'undefined'
                                ? document.body
                                : null
                        }
                        menuPosition="fixed"
                        styles={{
                            ...ReactSelectStyles,
                            menuPortal: (base) => ({
                                ...base,
                                zIndex: 9999,
                            }),
                        }}
                        disabled={disabled}
                    />

                    {managedEntityContractPhases.map((ecp, i) => {
                        const contractPhase = contractPhases.find(
                            (cp) => cp.id == ecp.contractPhaseId
                        );

                        const isLumpSum =
                            contractPhase &&
                            contractPhase.unitOfMeasure === 'LS';

                        if (
                            !contractPhase ||
                            isAlwaysAvailable(contractPhase)
                        ) {
                            return null;
                        }

                        const displayQuantity =
                            isLumpSum && ecp.targetQuantity != 0
                                ? ecp.targetQuantity * 100
                                : ecp.targetQuantity;

                        return (
                            <InputGroup key={`${uuid}-${i}`} className="mt-2">
                                <InputGroupText>
                                    {contractPhase.displayPhase} -{' '}
                                    {contractPhase.description}
                                </InputGroupText>

                                <Input
                                    value={displayQuantity}
                                    className={cls({
                                        invalid:
                                            ecp.targetQuantity === '' ||
                                            ecp.targetQuantity < 0,
                                    })}
                                    onChange={this.onInternalTargetQuantityChange.bind(
                                        this,
                                        { entityContractPhase: ecp }
                                    )}
                                />

                                <InputGroupText>
                                    {isLumpSum && <>% </>}
                                    {contractPhase.unitOfMeasure}
                                    {this.getQuantityQualifier(contractPhase)}
                                </InputGroupText>
                            </InputGroup>
                        );
                    })}
                </FormGroup>

                <ContractPhaseOverageWarning
                    entityContractPhases={managedEntityContractPhases}
                    contractPhases={contractPhases}
                />
            </>
        );
    }
}
